import React, { useState, useEffect } from 'react';
import {
  FiUsers, FiSearch, FiShield, FiLock, FiCheck, FiX, FiTrash2,
  FiAlertCircle, FiLoader, FiUserCheck
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import docsApi from '../../api/docsApi';

export function ApiShareModal({ doc, onClose }) {
  const [shares, setShares] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // New Share form
  const [selectedUser, setSelectedUser] = useState(null);
  const [canView, setCanView] = useState(true);
  const [canDownload, setCanDownload] = useState(true);
  const [canTest, setCanTest] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    loadShares();
  }, [doc.id]);

  const loadShares = async () => {
    try {
      setIsLoading(true);
      const res = await docsApi.getShares(doc.id);
      setShares(res || []);
    } catch (err) {
      toast.error('Failed to load sharing permissions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUserSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const res = await docsApi.searchUsers(query.trim());
      setSearchResults(res || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery('');
    setSearchResults([]);

    const existing = shares.find((s) => s.userId === user.id);
    if (existing) {
      setCanView(existing.canView);
      setCanDownload(existing.canDownload);
      setCanTest(existing.canTest);
      setCanEdit(existing.canEdit);
    } else {
      setCanView(true);
      setCanDownload(true);
      setCanTest(true);
      setCanEdit(false);
    }
  };

  const handleGrantAccess = async () => {
    if (!selectedUser) return;
    setIsSaving(true);
    setErrorMessage(null);

    try {
      await docsApi.saveShare(doc.id, {
        userId: selectedUser.id,
        canView,
        canDownload,
        canTest,
        canEdit,
      });

      toast.success(`Permissions updated for ${selectedUser.fullName || selectedUser.username}`);
      setSelectedUser(null);
      loadShares();
    } catch (err) {
      setErrorMessage(err.message || 'Failed to save permission');
      toast.error(err.message || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevoke = async (userId, username) => {
    if (!window.confirm(`Revoke all access for ${username}?`)) return;

    try {
      await docsApi.revokeShare(doc.id, userId);
      toast.success(`Access revoked for ${username}`);
      loadShares();
    } catch (err) {
      toast.error(err.message || 'Failed to revoke access');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden text-slate-900">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FiUsers className="text-blue-600" />
              Manage Sharing & Permissions
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              "{doc.title}" · Visibility:{' '}
              <span className="font-semibold text-slate-800">
                {doc.visibility}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          {errorMessage && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-50 text-rose-700 text-xs border border-rose-200">
              <FiAlertCircle size={15} />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Add User Section */}
          <div className="p-4 rounded-xl bg-slate-50/70 border border-slate-200 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <FiUserCheck size={14} className="text-blue-600" />
              Grant Access to Registered ERP User
            </h3>

            {!selectedUser ? (
              <div className="relative">
                <div className="relative">
                  <FiSearch size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleUserSearch(e.target.value)}
                    placeholder="Search user by name, username, or email..."
                    className="w-full text-xs pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-600 transition"
                  />
                  {isSearching && (
                    <FiLoader size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
                  )}
                </div>

                {searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-xl z-20 divide-y divide-slate-100">
                    {searchResults.map((u) => (
                      <div
                        key={u.id}
                        onClick={() => handleSelectUser(u)}
                        className="p-3 hover:bg-slate-50 cursor-pointer flex items-center justify-between text-xs transition"
                      >
                        <div>
                          <p className="font-semibold text-slate-900">
                            {u.fullName || u.username}
                          </p>
                          <p className="text-[11px] text-slate-500">{u.email} · {u.department || 'All'}</p>
                        </div>
                        <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-bold border border-slate-200">
                          {u.role}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs">
                <div>
                  <p className="font-bold text-blue-900">
                    {selectedUser.fullName || selectedUser.username}
                  </p>
                  <p className="text-[11px] text-slate-600">{selectedUser.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedUser(null)}
                  className="text-xs text-blue-700 hover:text-blue-900 font-semibold"
                >
                  Change User
                </button>
              </div>
            )}

            {/* Permission Checkboxes */}
            {selectedUser && (
              <div className="pt-2 space-y-3">
                <p className="text-xs font-bold text-slate-700">Select Permissions:</p>
                <div className="grid grid-cols-2 gap-2.5 text-xs">
                  <label className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition">
                    <input
                      type="checkbox"
                      checked={canView}
                      onChange={(e) => setCanView(e.target.checked)}
                      className="rounded text-blue-600"
                    />
                    <span className="font-medium text-slate-800">Can View</span>
                  </label>

                  <label className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition">
                    <input
                      type="checkbox"
                      checked={canDownload}
                      onChange={(e) => setCanDownload(e.target.checked)}
                      className="rounded text-blue-600"
                    />
                    <span className="font-medium text-slate-800">Can Download</span>
                  </label>

                  <label className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition">
                    <input
                      type="checkbox"
                      checked={canTest}
                      onChange={(e) => setCanTest(e.target.checked)}
                      className="rounded text-blue-600"
                    />
                    <span className="font-medium text-slate-800">Can Test API</span>
                  </label>

                  <label className="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer transition">
                    <input
                      type="checkbox"
                      checked={canEdit}
                      onChange={(e) => setCanEdit(e.target.checked)}
                      className="rounded text-blue-600"
                    />
                    <span className="font-medium text-slate-800">Can Edit / New Version</span>
                  </label>
                </div>

                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={handleGrantAccess}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg shadow-sm transition disabled:opacity-50"
                  >
                    {isSaving ? <FiLoader size={13} className="animate-spin" /> : <FiShield size={13} />}
                    <span>Save Permissions</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Current Shares List */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">
              Users with Explicit Access ({shares.length})
            </h3>

            {isLoading ? (
              <div className="flex justify-center p-6 text-slate-400">
                <FiLoader size={20} className="animate-spin text-blue-600" />
              </div>
            ) : shares.length === 0 ? (
              <div className="p-6 text-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 text-slate-500 text-xs">
                {doc.visibility === 'ORGANIZATION'
                  ? 'All organization members have default view access. No custom user overrides granted yet.'
                  : 'No users have been granted access yet. This documentation is currently visible only to you.'}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                    <tr>
                      <th className="p-3">User</th>
                      <th className="p-3">View</th>
                      <th className="p-3">Download</th>
                      <th className="p-3">Test</th>
                      <th className="p-3">Edit</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {shares.map((share) => (
                      <tr key={share.id} className="hover:bg-slate-50/50">
                        <td className="p-3">
                          <p className="font-bold text-slate-900">
                            {share.fullName || share.username}
                          </p>
                          <p className="text-[11px] text-slate-500">{share.email}</p>
                        </td>
                        <td className="p-3">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${share.canView ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        </td>
                        <td className="p-3">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${share.canDownload ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        </td>
                        <td className="p-3">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${share.canTest ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        </td>
                        <td className="p-3">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${share.canEdit ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        </td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => handleRevoke(share.userId, share.fullName || share.username)}
                            title="Revoke access immediately"
                            className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition"
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-4 border-t border-slate-100 bg-slate-50/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-slate-700 hover:text-slate-900 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ApiShareModal;
